import { ChangeEvent, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { saveAuthTokens } from 'src/api/axiosClient/tokenStorage';
import { normalizeUrl } from 'src/constants/helpers';
import { AuthContext } from 'src/contexts/AuthContext';
import { getCompanyTimezone } from 'src/modules/companies/helpers/company-timezone';
import { useSignupApi } from '../api';
import { errorMessage, isEmailTaken } from '../helpers/apiErrors';
import { clearSignupToken, getSignupToken } from '../helpers/signupSession';
import { timezoneForState } from '../helpers/stateTimezone';
import {
  isWebsite,
  signupCompanyInfoValidationSchema,
  signupCompanyInfoWithIndustryValidationSchema,
} from '../helpers/validation';
import { useSignupForm } from '../hooks';
import { SignupFormOptionsResponse, SignupState } from '../types';

export type SignupCompanyInfoFormValues = {
  companyName: string;
  displayName: string;
  entityType: string;
  vertical: string;
  ein: string;
  street: string;
  city: string;
  state: string;
  postalCode: string;
  website: string;
  timezone: string;
};

const EMPTY_FORM: SignupCompanyInfoFormValues = {
  companyName: '',
  displayName: '',
  entityType: '',
  vertical: '',
  ein: '',
  street: '',
  city: '',
  state: '',
  postalCode: '',
  website: '',
  // From the browser, and replaced by the company's own state below. Never on screen.
  timezone: getCompanyTimezone(),
};

/**
 * What became of a submit whose answer never arrived: nothing to act on and the failure stands as reported,
 * the client has been signed in from it, or the account is past this form and no session came back for it.
 */
type LostAnswer = 'reportFailure' | 'signedIn' | 'accountExists';

/**
 * A refusal this form cannot be pressed through. `accountReady` is our own account, raised by an attempt
 * whose answer was lost and past the window that trades the session for a login; `emailTaken` is an account
 * on that address that this funnel did not raise, and the address is frozen by the confirmation two screens
 * back. Both leave the form refused for good, and without an exit the only way off it is browser storage.
 */
export type SignupCompanyInfoBlock = 'accountReady' | 'emailTaken';

const ACCOUNT_READY_MESSAGE =
  'Your account is already set up. Please log in with the email and password you chose.';

// Only an address that passes gets the scheme: `http demo.com` would turn into `https://http demo.com`, which
// hides the typo behind something that looks like a website.
const withScheme = (website: string): string => (isWebsite(website) ? normalizeUrl(website) : website);

const EMPTY_OPTIONS: SignupFormOptionsResponse = {
  entityTypes: [],
  verticals: null,
};

/**
 * The last public step: these details create the company, the brand and the admin user, and its answer is
 * the login. Everything after it is company-scoped and behind the ordinary token.
 */
export const useSignupCompanyInfoViewController = () => {
  const api = useSignupApi();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const authContext = useContext(AuthContext);

  const [options, setOptions] = useState<SignupFormOptionsResponse>(EMPTY_OPTIONS);
  // Whether the industry is asked is only known from the answer, and the form is laid out around it.
  const [optionsLoading, setOptionsLoading] = useState(true);

  const {
    formValues,
    setFormValues,
    errors,
    formError,
    setFormError,
    submitting,
    setSubmitting,
    handleChange,
    validate,
  } = useSignupForm<SignupCompanyInfoFormValues>(
    EMPTY_FORM,
    options.verticals != null ? signupCompanyInfoWithIndustryValidationSchema : signupCompanyInfoValidationSchema,
  );
  const [blocked, setBlocked] = useState<SignupCompanyInfoBlock | null>(null);

  // A copy of the legal name follows it; the client's own answer never does.
  const displayNameChosen = useRef(false);

  const handleFieldChange = useCallback(
    (e: ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
      const { name, value } = e.target as { name: string; value: string };
      if (name === 'displayName') displayNameChosen.current = value.trim().length > 0;

      handleChange(e);

      // Filled in as the legal name is typed, so the client edits a suggestion instead of writing one.
      if (name === 'companyName' && !displayNameChosen.current) {
        setFormValues(current => ({ ...current, displayName: value }));
      }

      // The state says more about a company's hours than the browser it was opened in: a franchise opened
      // from head office, an agency filling the form for a client, anyone travelling. No field corrects it
      // any more — that is the point, and settings is where an unusual answer belongs.
      if (name === 'state') {
        const zone = timezoneForState(value);
        if (zone != null) setFormValues(current => ({ ...current, timezone: zone }));
      }
    },
    [handleChange, setFormValues],
  );

  const normalizeWebsite = useCallback(() => {
    setFormValues(current => ({ ...current, website: withScheme(current.website) }));
  }, [setFormValues]);

  const goToLogin = useCallback(() => navigate('/login'), [navigate]);

  // The token is dropped on the way: left in place, the plan screen offers to carry on with the very funnel
  // that cannot be finished.
  const startOver = useCallback(() => {
    clearSignupToken();
    navigate('/signup');
  }, [navigate]);

  /**
   * The browser may well be signed in as somebody else — the emailed link is opened wherever the mail was
   * read — so the previous account is dropped first, store and all: startup begins from the companies left in
   * the store, and asking for one the new token has no right to costs the session the client just got.
   */
  const signIn = useCallback((tokens: { accessToken: string; refreshToken: string }) => {
    dispatch({ type: 'logout' });
    localStorage.removeItem('bhiCurrentCompany');
    localStorage.removeItem('bhiUiSettings');

    authContext?.login();
    saveAuthTokens(tokens);
    clearSignupToken();
    // From here the screens carry the ordinary token and name the company in the path.
    navigate('/signup/phone-number');
  }, [authContext, dispatch, navigate]);

  useEffect(() => {
    if (getSignupToken() == null) {
      navigate('/signup');
      return;
    }

    let active = true;
    api
      .getFormOptions()
      .then(({ data }) => {
        if (!active) return;
        setOptions(data);
      })
      .catch(() => {
        if (active) setFormError('Form options could not be loaded.');
      })
      .finally(() => {
        if (active) setOptionsLoading(false);
      });

    return () => {
      active = false;
    };
  }, [api, navigate]);

  /**
   * The request failing is not the account failing: one the API accepted and answered into a dropped
   * connection leaves the account raised and this screen none the wiser, with a form that will be refused
   * from now on. The funnel is read before the failure is reported, and a session traded for the login the
   * lost answer was carrying.
   *
   * What says the step is over is the funnel refusing it, not `companyCreated`: the company row is written
   * part-way through provisioning, before the client is a member of it, so a failure at Telnyx or Stripe
   * leaves that flag set with the account still half-built. Pressing Continue again is what finishes it —
   * this screen must not trade it for a login instead.
   */
  const recoverFromLostAnswer = useCallback(async(): Promise<LostAnswer> => {
    let funnel: SignupState;
    try {
      funnel = (await api.getState()).data;
    } catch {
      // Nothing to act on. The failure below stands, and the next page load places the client by the funnel.
      return 'reportFailure';
    }

    if (funnel.allowedActions.includes('save_company_info')) return 'reportFailure';

    try {
      const { data: tokens } = await api.resumeSession();
      signIn(tokens);
      return 'signedIn';
    } catch {
      // The account is there and no session came back for it — that window is short on purpose. The password
      // the client chose is what gets them in from here, so the message says so instead of asking for the
      // form again, which the API now refuses.
      return 'accountExists';
    }
  }, [api, signIn]);

  const submit = useCallback(async() => {
    // The blur only shows the client the scheme; this is what makes sure the request carries it.
    const website = withScheme(formValues.website);
    setFormValues(current => ({ ...current, website: website }));
    if (!await validate({ ...formValues, website: website })) return;

    setSubmitting(true);
    setFormError(null);
    try {
      const { data } = await api.saveCompanyInfo({
        brand: {
          // Empty means "use the legal name", and the API is the one place that decides what that means.
          displayName: formValues.displayName.trim().length > 0 ? formValues.displayName : null,
          companyName: formValues.companyName,
          entityType: formValues.entityType,
          vertical: formValues.vertical.length > 0 ? formValues.vertical : null,
          ein: formValues.ein,
          einIssuingCountry: 'US',
          street: formValues.street,
          city: formValues.city,
          state: formValues.state,
          postalCode: formValues.postalCode,
          country: 'US',
          website: website.length > 0 ? website : null,
        },
        timezone: formValues.timezone,
      });

      signIn(data);
    } catch (e) {
      // Before the funnel is re-read: the step is still open, so the lost-answer path below would read this
      // as an ordinary failure and invite the same press again.
      if (isEmailTaken(e)) {
        setBlocked('emailTaken');
        setFormError(errorMessage(e, 'An account with this email already exists.'));
        setSubmitting(false);
        return;
      }

      const recovery = await recoverFromLostAnswer();
      if (recovery === 'signedIn') return;

      if (recovery === 'accountExists') setBlocked('accountReady');
      setFormError(recovery === 'accountExists'
        ? ACCOUNT_READY_MESSAGE
        : errorMessage(e, 'Your details could not be saved. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  }, [api, formValues, recoverFromLostAnswer, setFormError, setFormValues, setSubmitting, signIn, validate]);

  return {
    formValues,
    options,
    optionsLoading,
    errors,
    formError,
    submitting,
    blocked,
    handleChange: handleFieldChange,
    normalizeWebsite,
    submit,
    goToLogin,
    startOver,
  };
};
